import React, { useEffect, useState } from "react";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  CardContent,
  Tooltip,
  Button,
  Modal,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  getJewelleryOrdersRequest,
  changeJewelleryOrderStatusRequest,
} from "../../../redux/Jewellery/JewellerySlice";
import { CiEdit } from "react-icons/ci";
import { canShowPermission } from "../Permissions/permissionObject";
import useCsvConverter from "../../../hooks/convertToCSV";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";

const toDateParam = (date, boundary) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  if (boundary === "end") {
    d.setHours(23, 59, 59, 999);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return d.toISOString();
};

const customerName = (customer) => {
  if (!customer) return "N.A";
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return name || "N.A";
};

const statusPresentation = (status) => {
  switch (status) {
    case "SUCCESS":
      return { color: "green", text: "ORDER DELIVERED" };
    case "PENDING":
      return { color: "blue", text: "PENDING" };
    case "DISPATCHED":
      return { color: "orange", text: "ORDER DISPATCHED" };
    case "READYTODISPATCH":
      return { color: "orange", text: "READY TO DISPATCH" };
    case "RETURNED":
      return { color: "red", text: "ORDER RETURNED" };
    case "REFUNDCOMPLETED":
      return { color: "green", text: "REFUND COMPLETED" };
    case "CANCELLED":
      return { color: "red", text: "CANCELLED" };
    case "FAILED":
      return { color: "red", text: "FAILED" };
    case "captured":
      return { color: "green", text: "CAPTURED" };
    case "authorized":
      return { color: "blue", text: "AUTHORIZED" };
    default:
      return { color: "#333", text: status || "N.A" };
  }
};

const JewelleryOrder = () => {
  const dispatch = useDispatch();
  const jewelleryState = useSelector((state) => state.jewellery || {});
  const {
    jewelleryOrders: data = [],
    ordersTotal: total = 0,
    loading = false,
  } = jewelleryState;
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState("");
  const [appliedRange, setAppliedRange] = useState({ startDate: "", endDate: "" });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);

  const hasJewelleryPermission = Object.prototype.hasOwnProperty.call(
    permissions,
    "JewelleryOrder"
  );
  const canEdit = hasJewelleryPermission
    ? canShowPermission(permissions, "JewelleryOrder", "edit")
    : canShowPermission(permissions, "ProductOrder", "edit");

  useEffect(() => {
    dispatch(
      getJewelleryOrdersRequest({
        page: pagination.page,
        limit: pagination.limit,
        search,
        startDate: appliedRange.startDate,
        endDate: appliedRange.endDate,
      })
    );
  }, [dispatch, pagination, search, appliedRange]);

  const handleGetDetails = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start Date cannot be after End Date");
      return;
    }
    setPagination((prev) => ({ ...prev, page: 0 }));
    setAppliedRange({
      startDate: toDateParam(startDate, "start"),
      endDate: toDateParam(endDate, "end"),
    });
  };

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case "changePage":
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;
      case "changeRowsPerPage":
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;
      case "search":
        setSearch(tableState.searchText || "");
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;
      default:
        break;
    }
  };

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start Date cannot be after End Date");
      return;
    }

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/artificial-jewellery/artificialJewelleryOrdersCsv",
        query: {
          startDate: toDateParam(startDate, "start"),
          endDate: toDateParam(endDate, "end"),
        },
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "jewelleryOrders.csv");
        },
      }),
    );
  };

  const submitStatus = () => {
    if (!selectedOrderStatus) {
      toast.error("Please select a status.");
      return;
    }

    dispatch(
      changeJewelleryOrderStatusRequest({
        order_id: selectedOrderId,
        status: selectedOrderStatus,
        page: pagination.page,
        limit: pagination.limit,
        search,
        startDate: appliedRange.startDate,
        endDate: appliedRange.endDate,
      })
    );

    setStatusModalOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderStatus("");
  };

  const columns = [
    {
      field: "edit",
      headerName: "Edit",
      hideInExcel: true,
      render: (val, row) => {
        const status = String(row.status || "").toUpperCase();
        if (status === "CANCELLED") return " ";

        return canEdit ? (
          <CiEdit
            style={{
              cursor: "pointer",
              color: "#0a0b0cff",
              fontSize: "28px",
            }}
            onClick={() => {
              setSelectedOrderId(row._id);
              setSelectedOrderStatus("");
              setStatusModalOpen(true);
            }}
          />
        ) : (
          <span style={{ fontSize: "12px", color: "#aaa" }}>No Permission</span>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Date",
      render: (val, row) => (
        <Tooltip title={row.createdAt || "N.A"} arrow>
          <span>{row.createdAt || "N.A"}</span>
        </Tooltip>
      ),
    },
    {
      field: "orderId",
      headerName: "Order Id",
      render: (val, row) => row.orderId || "N.A",
    },
    {
      field: "customer",
      headerName: "Name",
      render: (val, row) => row.customer || "N.A",
    },
    {
      field: "phone",
      headerName: "Phone",
      render: (val, row) => row.phone || "N.A",
    },
    {
      field: "name",
      headerName: "Product",
      render: (val, row) => row.name || "N.A",
    },
    {
      field: "productCode",
      headerName: "Product Code",
      render: (val, row) => row.productCode || "N.A",
    },
    {
      field: "quantity",
      headerName: "Qty",
      render: (val, row) => row.quantity ?? "N.A",
    },
    {
      field: "finalAmount",
      headerName: "Amount",
      render: (val, row) => row.finalAmount ?? "0.00",
    },
    {
      field: "image",
      headerName: "Product Image",
      render: (val, row) => {
        const image = row.files?.[0];
        if (!image) return "N.A";
        return (
          <Button
            onClick={() => {
              setSelectedProduct({
                url: image,
                name: row.name,
                productCode: row.productCode,
                mrp: row.mrp,
              });
              setOpenModal(true);
            }}
            variant="contained"
            color="warning"
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: "bold",
            }}
          >
            View
          </Button>
        );
      },
    },
    {
      field: "status",
      headerName: "Order Status",
      flex: 1,
      render: (val, row) => {
        const { color, text } = statusPresentation(row.status);
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              fontSize: "14px",
              fontWeight: "bold",
              color,
            }}
          >
            {text}
          </div>
        );
      },
    },
    {
      field: "approvedBy",
      headerName: "Updated By",
      render: (val, row) => row.approvedBy || "N.A",
    },
    {
      field: "customernote",
      headerName: "Customer Note",
      render: (val, row) => row.customernote || "N.A",
    },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Jewellery Orders
        </Typography>

        <CardContent sx={{ height: "100%", width: "100%" }}>
          <Box display="flex" gap={1.5} px={2} pb={1} flexWrap="wrap" alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                gap={1.5}
                px={2}
                py={1}
              >
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  maxDate={endDate || new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  fullWidth={isMobile}
                  sx={{
                    height: 36,
                    minWidth: 120,
                    fontSize: 13,
                    textTransform: "none",
                    px: 2,
                    whiteSpace: "nowrap",
                  }}
                  onClick={handleGetDetails}
                >
                  Get Details
                </Button>
              </Box>
            </LocalizationProvider>
          </Box>
          <TableCompo
            columns={columns}
            data={(data || []).map((row) => ({
              ...row,
              createdAt: row.createdAt
                ? new Date(row.createdAt).toLocaleDateString("en-IN")
                : "N.A",
              customer: customerName(row.customer),
              phone: row.customer?.phone || "N.A",
              finalAmount: (
                (Number(row.finalAmount) || 0) * (Number(row.quantity) || 1)
              ).toFixed(2),
            }))}
            totalCount={total}
            loading={loading}
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
            extraButtons={
              <Button size="sm" variant="outlined" onClick={handleDownloadCsv}>
                Download CSV
              </Button>
            }
          />

          <Modal open={openModal} onClose={() => setOpenModal(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                maxWidth: 500,
                textAlign: "center",
              }}
            >
              {selectedProduct ? (
                <>
                  <img
                    src={selectedProduct.url}
                    alt={selectedProduct.name}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      maxHeight: "400px",
                      objectFit: "contain",
                      marginBottom: "16px",
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold">
                    {selectedProduct.name}
                  </Typography>
                  {selectedProduct.productCode ? (
                    <Typography variant="body2" color="text.secondary">
                      {selectedProduct.productCode}
                    </Typography>
                  ) : null}
                  <Typography variant="subtitle1" color="primary" mt={1}>
                    ₹{selectedProduct.mrp}
                  </Typography>
                </>
              ) : (
                <p>No product details available</p>
              )}
            </Box>
          </Modal>

          <Modal open={statusModalOpen} onClose={() => setStatusModalOpen(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                minWidth: 400,
              }}
            >
              <Typography variant="h6" mb={2}>
                Jewellery Order Details
              </Typography>

              <select
                value={selectedOrderStatus}
                onChange={(e) => setSelectedOrderStatus(e.target.value)}
                style={{ width: "100%", padding: "8px", marginBottom: "16px" }}
              >
                <option value="">Select Status</option>
                <option value="READYTODISPATCH">READY TO DISPATCH</option>
                <option value="DISPATCHED">ORDER DISPATCHED</option>
                <option value="APPROVED">ORDER DELIVERED</option>
                <option value="RETURNED">ORDER RETURNED</option>
                <option value="REFUNDCOMPLETED">REFUND COMPLETED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>

              <Button variant="contained" onClick={submitStatus} fullWidth>
                Submit
              </Button>
            </Box>
          </Modal>
        </CardContent>
      </Box>
    </Box>
  );
};

export default JewelleryOrder;
